import cloneDeep from 'lodash/cloneDeep';
import React, { useEffect, useRef, useState } from 'react';
import { Tabs } from '@meitu/saas-ui';
import { ITabItemProps } from '@meitu/saas-ui/es/tabs/interface';
import {
    defaultPosterizeEnhance,
    IPosterizeEnhance,
    type ImageEnhancePlugin,
} from '@meitu/saas-editor-core/es/layers/main-layer/plugins/apps/user-editor/image-enhance';
import { useLocaleStore } from '@/i18n';
import { useBatchActionHooks } from '../batch-action-hooks';
import EnhanceSlider from '../enhance-slider';
import Reset from '../reset';
import SelectColor from '../select-color';
import { IEnhanceFeatureProps, IEnhanceSlider } from '../types';
import { getSliderConfig } from './config';

interface ITabContent extends Pick<IEnhanceFeatureProps, 'theme' | 'onSelect' | 'editor' | 'trackFunc'> {
    type: TabType;
    posterizeEnhance: IPosterizeEnhance;
    onEnhance: (keyName: string, val: number) => void;
    onBeforeEnhance: (keyName: string, val: number) => void;
    onAfterEnhance: (keyName: string, val: number, type: string) => void;
    isBatchEditor?: boolean;
    setStageLoading?: (stageIndex: number, loading: boolean) => void;
    batchSetIsLoading?: (stageIndex: number[]) => void;
    setIsDisable?: (isDisable: boolean) => void;
}

export const getPosterizeArgs = (p: IPosterizeEnhance) => {
    return {
        renderTabIndex: p.renderTabIndex,
        color: {
            posterize_light: p.color.posterize_light[p.color.posterize_light_color],
            posterize_light_color: p.color.posterize_light_color,
            posterize_shadow: p.color.posterize_shadow[p.color.posterize_shadow_color],
            posterize_shadow_color: p.color.posterize_shadow_color,
        },
    };
};

const TabContent: React.FC<ITabContent> = ({
    editor,
    theme,
    type,
    posterizeEnhance,
    onBeforeEnhance,
    onEnhance,
    onAfterEnhance,
    onSelect,
    trackFunc,
    isBatchEditor,
    setStageLoading,
    batchSetIsLoading,
    setIsDisable,
}) => {
    const skinList = useRef<Array<IEnhanceSlider[]>>();
    const [sliderSkin, setSliderSkin] = useState<IEnhanceSlider[]>([]);
    const { batchAction } = useBatchActionHooks();
    const [lightColorIndex, setLightColorIndex] = useState(posterizeEnhance.color.posterize_light_color);
    const [shadowColorIndex, setShadowColorIndex] = useState(posterizeEnhance.color.posterize_shadow_color);

    const curTabDataName = `posterize_${type}`;
    const curColorName = `${curTabDataName}_color`;

    useEffect(() => {
        setLightColorIndex(posterizeEnhance.color.posterize_light_color);
        setShadowColorIndex(posterizeEnhance.color.posterize_shadow_color);
    }, [posterizeEnhance.renderTabIndex]);

    useEffect(() => {
        setLightColorIndex(posterizeEnhance.color.posterize_light_color);
    }, [posterizeEnhance.color.posterize_light_color]);

    useEffect(() => {
        setShadowColorIndex(posterizeEnhance.color.posterize_shadow_color);
    }, [posterizeEnhance.color.posterize_shadow_color]);

    useEffect(() => {
        skinList.current = getSliderConfig(theme);
        if (type === TabType.LIGHT) {
            setSliderSkin(skinList.current[lightColorIndex]);
        } else {
            setSliderSkin(skinList.current[shadowColorIndex]);
        }
    }, [theme, type, lightColorIndex, shadowColorIndex]);

    const curColorIndex = type === TabType.LIGHT ? lightColorIndex : shadowColorIndex;

    return (
        <div style={{ paddingTop: 12 }}>
            <SelectColor
                index={curColorIndex}
                onSelectIndex={(index) => {
                    if (type === TabType.LIGHT) {
                        setLightColorIndex(index);
                    } else {
                        setShadowColorIndex(index);
                    }
                    onSelect?.(index, type);
                }}
            />
            {sliderSkin.map((v, i) => {
                return (
                    <EnhanceSlider
                        key={i}
                        maxVal={v.maxVal}
                        minVal={v.minVal}
                        value={posterizeEnhance.color[curTabDataName][curColorIndex]}
                        title={v.name}
                        opacityOpposite={v.opacityOpposite}
                        onChange={async (val: number) => {
                            const newParams = cloneDeep(posterizeEnhance);

                            newParams.color[curTabDataName] = [0, 0, 0, 0, 0, 0];
                            newParams.color[curTabDataName][curColorIndex] = val;
                            newParams.color[curColorName] = curColorIndex;

                            onBeforeEnhance?.(v.keyName, val);
                            editor.getInstance<ImageEnhancePlugin>('ImageEnhancePlugin').setPosterizeEnhance(newParams);
                            await editor
                                .getInstance<ImageEnhancePlugin>('ImageEnhancePlugin')
                                .basicEnhance.action(getPosterizeArgs(newParams), trackFunc);
                            onEnhance?.(v.keyName, val);
                        }}
                        onAfterChange={async (val) => {
                            if (isBatchEditor) {
                                const newParams = cloneDeep(posterizeEnhance);
                                newParams.color[curTabDataName] = [0, 0, 0, 0, 0, 0];
                                newParams.color[curTabDataName][curColorIndex] = val;
                                newParams.color[curColorName] = curColorIndex;
                                const params = getPosterizeArgs(newParams);
                                batchAction(
                                    editor,
                                    params,
                                    (stageIndex: number, isBatch: boolean) => {
                                        setStageLoading?.(stageIndex, isBatch);
                                    },
                                    setIsDisable,
                                    batchSetIsLoading,
                                );
                            }
                            onAfterEnhance?.(
                                v.keyName,
                                val,
                                type === TabType.LIGHT ? 'posterize_light' : 'posterize_shadow',
                            );
                        }}
                        background={v.background}
                        wrapBackground={v.wrapBackground}
                    />
                );
            })}
        </div>
    );
};

enum TabType {
    LIGHT = 'light',
    SHADOW = 'shadow',
}

export const PosterizeFeature: React.FC<IEnhanceFeatureProps> = ({
    editor,
    theme,
    onReset,
    onBeforeEnhance,
    onEnhance,
    onSelect,
    onTab,
    onAfterEnhance,
    trackFunc,
    isBatchEditor,
    setStageLoading,
    batchSetIsLoading,
    setIsDisable,
}) => {
    const { $t } = useLocaleStore();

    const [enhanceState, _setEnhanceState] = useState(
        editor.getInstance<ImageEnhancePlugin>('ImageEnhancePlugin').getState(),
    );

    useEffect(() => {
        return editor.getInstance<ImageEnhancePlugin>('ImageEnhancePlugin').watch((state) => _setEnhanceState(state));
    }, [editor]);

    const { posterizeEnhance } = enhanceState;

    const handleEnhance = async (keyName: string, val: number) => {
        onEnhance?.(keyName, val);
    };

    const handleBeforeEnhance = (keyName: string, val: number) => {
        onBeforeEnhance?.(keyName, val);
    };

    const handleAfterEnhance = (keyName: string, val: number, type: string) => {
        onAfterEnhance?.(keyName, val, type);
    };

    const handelTabClick = (target: string) => {
        const curData = cloneDeep(posterizeEnhance);

        curData.renderTabIndex = target === TabType.LIGHT ? 1 : 2;
        editor.getInstance<ImageEnhancePlugin>('ImageEnhancePlugin').setPosterizeEnhance(curData);
        onTab?.(target);
    };

    const tabs: ITabItemProps[] = [
        {
            name: $t((v) => v.mainLayer.posterSize.light),
            tabKey: TabType.LIGHT,
            content: (
                <TabContent
                    theme={theme}
                    type={TabType.LIGHT}
                    posterizeEnhance={posterizeEnhance}
                    onSelect={onSelect}
                    onBeforeEnhance={handleBeforeEnhance}
                    onEnhance={handleEnhance}
                    onAfterEnhance={handleAfterEnhance}
                    editor={editor}
                    trackFunc={trackFunc}
                    isBatchEditor={isBatchEditor}
                    setStageLoading={setStageLoading}
                    batchSetIsLoading={batchSetIsLoading}
                    setIsDisable={setIsDisable}
                />
            ),
        },
        {
            name: $t((v) => v.mainLayer.posterSize.shadow),
            tabKey: TabType.SHADOW,
            content: (
                <TabContent
                    theme={theme}
                    type={TabType.SHADOW}
                    posterizeEnhance={posterizeEnhance}
                    onSelect={onSelect}
                    onBeforeEnhance={handleBeforeEnhance}
                    onEnhance={handleEnhance}
                    onAfterEnhance={handleAfterEnhance}
                    editor={editor}
                    trackFunc={trackFunc}
                    isBatchEditor={isBatchEditor}
                    setStageLoading={setStageLoading}
                    batchSetIsLoading={batchSetIsLoading}
                    setIsDisable={setIsDisable}
                />
            ),
        },
    ];

    return (
        <>
            <Tabs
                data={tabs}
                activeIndex={posterizeEnhance.renderTabIndex}
                tabsHeight="28px"
                handleTabClick={handelTabClick}
            />
            <Reset
                onClick={async () => {
                    const newParams = cloneDeep(defaultPosterizeEnhance);

                    editor.getInstance<ImageEnhancePlugin>('ImageEnhancePlugin').setPosterizeEnhance(newParams);

                    if (isBatchEditor) {
                        setIsDisable?.(true);
                        await editor.getInstance<ImageEnhancePlugin>('ImageEnhancePlugin').basicEnhance.batchReset();
                        setIsDisable?.(false);
                        onReset?.();
                        return;
                    }
                    editor
                        .getInstance<ImageEnhancePlugin>('ImageEnhancePlugin')
                        .basicEnhance.action(getPosterizeArgs(newParams));
                    onReset?.();
                }}
            />
        </>
    );
};
